import type { TOC } from '@ember/component/template-only';
import { fn, hash } from '@ember/helper';
import {
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismLoadingBar,
  PrismMessage,
  PrismSelectInput,
  PrismToggle,
  PrismViewSwitcher,
} from '@mylighthouse/prism-ember';
import { findBy } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import GeneralChartsChartUpdater from 'lumen/components/general/charts/chart-updater';
import GeneralCurrencyPicker from 'lumen/components/general/currency-picker';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralFilterBarDivider from 'lumen/components/general/filter-bar/divider';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import MarketTotalRevenueChartTrend from 'lumen/components/market/total-revenue/chart-trend';
import MarketTotalRevenueContributionBreakdown from 'lumen/components/market/total-revenue/contribution-breakdown';
import MarketTotalRevenueElegantDateHandling from 'lumen/components/market/total-revenue/elegant-date-handling';
import MarketTotalRevenueKpiSection from 'lumen/components/market/total-revenue/kpi-section';
import MarketTotalRevenueReferencePicker from 'lumen/components/market/total-revenue/reference-picker';
import MarketTotalRevenueStrategyChartSegment from 'lumen/components/market/total-revenue/strategy/chart-segment';
import MarketTotalRevenueStrategyTableSegment from 'lumen/components/market/total-revenue/strategy/table-segment';
import MarketTotalRevenueTablePortfolio from 'lumen/components/market/total-revenue/table-portfolio';
import type MarketTotalRevenueController from 'lumen/controllers/market/total-revenue';
import hasFeatureToggle from 'lumen/helpers/has-feature-toggle';

interface MarketTotalRevenueSignature {
  Args: {
    controller: MarketTotalRevenueController;
    model: unknown;
  };
}

<template>
  <MarketTotalRevenueElegantDateHandling
    @currentPeriod={{hash
      from=@controller.dateRange.start
      to=@controller.dateRange.end
    }}
    @properties={{@controller.selectedProperties}}
    @referencePeriods={{@controller.referencePeriodPickerReferencePeriods}}
    as |elegantDateHandling|
  >
    <GeneralPageHeader class="total-revenue">
      <div class="content-wrapper flex items-center justify-between">
        <div class="flex flex-1 items-center gap-300">
          {{! @glint-expect-error: Incorrect type }}
          <GeneralDatepickersDatepickerWithSidebar
            @maxDate={{@controller.totalRevenueDate.maxDateInLocalTime}}
            @maxRange="2 years"
            @minDate={{@controller.totalRevenueDate.minDateInLocalTime}}
            @range={{@controller.dateRange}}
            @rangeChanged={{@controller.updateDateRange}}
            @sidebarLists={{@controller.datepickerSidebarLists}}
          />

          {{#if
            elegantDateHandling.hasPropertiesWithPartialDataInCurrentPeriod
          }}
            <PrismButton
              @buttonType="critical-secondary"
              @icon="warning"
              @onClick={{fn
                (mut @controller.isShowingIncompleteDataModal)
                true
              }}
            />
          {{/if}}

          <MarketTotalRevenueReferencePicker
            @maxDate={{@controller.referencePeriodPickerMaxDate}}
            @minDate={{@controller.totalRevenueDate.minDateInLocalTime}}
            @nrOfDays={{@controller.dateRangeNumberOfDays}}
            @onSelectedReferencesChange={{@controller.updateReferencePeriods}}
            @selectedReferences={{@controller.referencePeriodPickerReferencePeriods}}
          />

          {{#if
            elegantDateHandling.hasPropertiesWithPartialDataInReferencePeriod
          }}
            <PrismButton
              @buttonType="critical-secondary"
              @icon="warning"
              @onClick={{fn
                (mut @controller.isShowingIncompleteDataModal)
                true
              }}
            />
          {{/if}}
        </div>

        <div>
          {{#if (hasFeatureToggle "total-revenue-strategy-tab")}}
            {{#if @controller.hasHotelsWithReservationLink}}
              <PrismViewSwitcher
                @activeButtonId={{@controller.view}}
                @onSetActiveButtonId={{@controller.updateView}}
                as |ViewSwitcher|
              >
                <ViewSwitcher.button
                  @id="departments"
                  @label="Departments"
                  data-test-id="view-switcher-button-department"
                />
                <ViewSwitcher.button
                  @id="strategy"
                  @label="Strategy"
                  data-test-id="view-switcher-button-strategy"
                />
              </PrismViewSwitcher>
            {{/if}}
          {{/if}}
        </div>

        <div class="flex flex-1 items-center justify-end gap-200">
          {{#if @controller.isDepartmentsView}}
            <PrismToggle
              @dataTestId="per-available-room-toggle"
              @isChecked={{@controller.totalRevenue.isPerAvailableRoom}}
              @label={{t "total_revenue.view_per_available_room"}}
              @onChange={{@controller.handlePerAvailableRoomToggleChange}}
            />

            <GeneralFilterBarDivider />
          {{/if}}

          <GeneralCurrencyPicker
            @defaultCurrencyTooltip={{t
              "revenue_insight.currency.default_in_use"
            }}
            @fallbackCurrencyCode={{@controller.hotelAndMarketService.market.revenueInsightCurrency}}
            @onCurrencyChange={{@controller.onCurrencyChange}}
          />
        </div>
      </div>
    </GeneralPageHeader>

    <GeneralPageHeader @isSmall={{true}}>
      <div class="content-wrapper flex items-center">
        <PrismComboSelectInput
          @buildLabelWithCount={{@controller.portfolioBuildLabelWithCount}}
          @isSearchable={{true}}
          @onChange={{@controller.choosePortfolio}}
          @options={{@controller.portfolioFilterOptions}}
          @selectAllLabel={{t "total_revenue.portfolio.count.any"}}
          @value={{@controller.portfolioFilterValue}}
        />

        <PrismComboSelectInput
          @buildLabelWithCount={{@controller.propertyBuildLabelWithCount}}
          @isSearchable={{true}}
          @onChange={{@controller.chooseProperty}}
          @options={{@controller.propertyFilterOptions}}
          @selectAllLabel={{t "total_revenue.property.count.any"}}
          @unsafe_selectAllWarningMessage={{t
            "total_revenue.property.select_all.warning"
          }}
          @value={{@controller.propertyFilterValue}}
        />

        {{! @glint-expect-error: Incorrect type }}
        <GeneralFilterBarFilterBar
          @activeFilters={{@controller.activeFilters}}
          @filterBarSegmentationClicked={{@controller.handleFilterBarRemove}}
          @hideFavourites={{true}}
          @isLoading={{@controller.totalRevenueMyhotels.myHotelsTaskInstance.isRunning}}
          @side="right"
        />
      </div>
    </GeneralPageHeader>

    <GeneralPageMain class="total-revenue">
      {{#if
        (or
          @controller.totalRevenueMyhotels.myHotelsTaskInstance.isRunning
          @controller.isDashboardLoading
        )
      }}
        <PrismLoadingBar
          @isFixed={{true}}
          @positionTop={{@controller.mainLoadingBarPositionTop}}
        />
      {{/if}}
      <div class="content-wrapper main-content flex flex-col gap-800">
        {{#if @controller.canShowIncompleteDataBanner}}
          <elegantDateHandling.Banner
            {{! @glint-expect-error: Incorrect type }}
            @closeBanner={{fn
              (mut @controller.canShowIncompleteDataBanner)
              false
            }}
            @showElegantDateHandlingModal={{fn
              (mut @controller.isShowingIncompleteDataModal)
              true
            }}
          />
        {{/if}}

        <MarketTotalRevenueKpiSection
          @isLoading={{@controller.isDashboardLoading}}
          @isPerAvailableRoom={{@controller.totalRevenue.isPerAvailableRoom}}
          @segmentationBreakdownSummary={{@controller.model.segmentationBreakdowns.last.value.summary}}
          @summary={{@controller.model.totalRevenueDashboard.last.value.summary}}
          @view={{@controller.view}}
        />

        {{#if @controller.isDepartmentsView}}
          <section class="flex flex-col gap-400">
            <div class="flex items-center justify-between">
              <div
                class="text-neutral-emphasis mr-200 flex items-center gap-200"
              >
                <h2>{{t "total_revenue.portfolio"}}</h2>
                {{#if @controller.numberOfHotels}}
                  <span class="text-300--regular">
                    {{t "general.hotel.count" count=@controller.numberOfHotels}}
                  </span>
                {{/if}}
              </div>
            </div>

            <MarketTotalRevenueTablePortfolio
              @focusedProperties={{@controller.focusedProperties}}
              @isLoading={{@controller.model.totalRevenueDashboard.isRunning}}
              @isPerAvailableRoom={{@controller.isPerAvailableRoom}}
              @onPropertyClick={{@controller.handleFocusProperty}}
              @portfolio={{@controller.model.totalRevenueDashboard.last.value.portfolioList}}
            />
          </section>

          <section class="flex flex-col gap-400">
            <div class="flex items-center justify-between">
              <h2 class="text-neutral-emphasis mr-200 flex items-center">
                {{t "total_revenue.trend_analysis"}}
              </h2>
              <div class="text-grey ml-300 flex items-center gap-200">
                <PrismSelectInput
                  @onChange={{@controller.updateTrendMetric}}
                  @options={{@controller.trendMetricOptions}}
                  @value={{findBy
                    "id"
                    @controller.trendMetric
                    @controller.trendMetricOptions
                  }}
                />
                <PrismSelectInput
                  @onChange={{@controller.updateTrendAggregation}}
                  @options={{@controller.trendAggregationOptions}}
                  @value={{findBy
                    "id"
                    @controller.trendAggregation
                    @controller.trendAggregationOptions
                  }}
                />
              </div>
            </div>

            <MarketTotalRevenueChartTrend
              @aggregation={{@controller.trendAggregation}}
              @chartSelectionChanged={{@controller.updateDateFilter}}
              @data={{@controller.model.totalRevenueTrend.last.value}}
              @isLoading={{@controller.model.totalRevenueTrend.isRunning}}
              @isPerAvailableRoom={{@controller.totalRevenue.isPerAvailableRoom}}
              @metric={{@controller.trendMetric}}
            />
          </section>

          <section class="flex flex-col gap-400">
            <div class="flex items-center justify-between">
              <h2>{{t "total_revenue.breakdowns"}}</h2>
              <PrismSelectInput
                @onChange={{@controller.updateBreakdownLocalReference}}
                @options={{@controller.localReferenceOptions}}
                @value={{@controller.selectedLocalReferenceOption}}
              />
            </div>

            <MarketTotalRevenueContributionBreakdown
              @departments={{@controller.model.totalRevenueDashboard.last.value.departmentList}}
              @exportSourceTransactions={{@controller.exportSourceTransactions}}
              @isLoading={{@controller.model.totalRevenueDashboard.isRunning}}
              @selectedDepartmentId={{@controller.departmentId}}
              @selectedReferenceType={{@controller.localReference}}
              @summary={{@controller.model.totalRevenueDashboard.last.value.summary}}
              @updateSelectedDepartment={{@controller.updateSelectedDepartmentId}}
            />
          </section>
        {{/if}}

        {{#if @controller.isStrategyView}}
          <section class="flex flex-col gap-400">
            <div class="flex items-center justify-between">
              <h2>{{t "total_revenue.production_by_type"}}</h2>
              <PrismSelectInput
                @onChange={{@controller.updateStrategyLocalReference}}
                @options={{@controller.localReferenceOptions}}
                @value={{@controller.selectedLocalReferenceOption}}
              />
            </div>

            <GeneralChartsChartUpdater as |chartUpdater|>
              <PrismCard @contentLayout="noPadding">
                <:content>
                  {{#if @controller.isSegmentationBreakdownEmptyStateShown}}
                    <div class="flex h-[340px] items-center justify-center">
                      <PrismMessage
                        @description=""
                        @illustration="100x100_binoculars"
                        @title={{t
                          "total_revenue.production_by_type.empty_state.title"
                        }}
                      />
                    </div>
                  {{else}}
                    <div class="p-400">
                      <MarketTotalRevenueStrategyChartSegment
                        @onUpdateChartReference={{chartUpdater.onUpdateChartReference}}
                        @segmentationBreakdownItems={{@controller.segmentationBreakdownItems}}
                        @selectedReferenceType={{@controller.localReference}}
                      />

                      <MarketTotalRevenueStrategyTableSegment
                        @isLoading={{@controller.model.segmentationBreakdowns.isRunning}}
                        @segmentationBreakdownItems={{@controller.segmentationBreakdownItems}}
                        @selectedReferenceType={{@controller.localReference}}
                      />
                    </div>
                  {{/if}}
                </:content>
              </PrismCard>
            </GeneralChartsChartUpdater>
          </section>
        {{/if}}
      </div>
    </GeneralPageMain>

    {{#if @controller.isShowingIncompleteDataModal}}
      <elegantDateHandling.Modal
        {{! @glint-expect-error: Incorrect type }}
        @onClose={{fn (mut @controller.isShowingIncompleteDataModal) false}}
        @onRemovePropertiesFromFilter={{@controller.removePropertiesFromFilter}}
      />
    {{/if}}
  </MarketTotalRevenueElegantDateHandling>

  {{outlet}}
</template> satisfies TOC<MarketTotalRevenueSignature>;
